// Shared UI primitives. Everything interactive clears a 44px touch target, because
// the whole app is designed to be used one-handed on a phone before coffee.

import type { ChangeEvent, ReactNode } from 'react';

export function Card({ children, className = '', as: Tag = 'section' }: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'article';
}) {
  return <Tag className={`card p-4 ${className}`}>{children}</Tag>;
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted">{children}</h2>
      {action}
    </div>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-on-accent border-transparent',
  secondary: 'bg-surface text-ink border-line',
  ghost: 'bg-transparent text-ink-2 border-transparent',
  danger: 'bg-transparent text-critical border-critical',
};

export function Button({
  children, onClick, variant = 'secondary', type = 'button', disabled, className = '', full,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
  full?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`min-h-11 px-4 rounded-xl border text-sm font-medium transition-opacity
        disabled:opacity-40 disabled:cursor-not-allowed active:opacity-80
        ${BUTTON_STYLES[variant]} ${full ? 'w-full' : ''} ${className}`}
    >
      {children}
    </button>
  );
}

/** A toggle chip. Selection is shown by fill *and* a check, never colour alone. */
export function Chip({ label, selected, onClick, tone = 'accent' }: {
  label: string;
  selected: boolean;
  onClick: () => void;
  tone?: 'accent' | 'warning';
}) {
  const on = tone === 'warning'
    ? 'bg-warning/15 border-warning text-ink'
    : 'bg-accent-soft border-accent text-ink';
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`min-h-11 px-3.5 rounded-full border text-sm transition-colors
        ${selected ? on : 'bg-surface border-hairline text-ink-2'}`}
    >
      {selected ? '✓ ' : ''}{label}
    </button>
  );
}

export function Field({ label, hint, children, required }: {
  label: string;
  hint?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1.5">
        {label}{required && <span className="text-muted font-normal"> · required</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-muted mt-1.5">{hint}</span>}
    </label>
  );
}

const INPUT_CLASS =
  'w-full min-h-11 px-3 rounded-xl bg-surface border border-hairline text-ink ' +
  'placeholder:text-muted focus:border-accent outline-none';

export function TextInput({ value, onChange, placeholder, type = 'text', inputMode, autoComplete, max, min }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email';
  autoComplete?: string;
  max?: string | number;
  min?: string | number;
}) {
  return (
    <input
      type={type}
      value={value}
      inputMode={inputMode}
      autoComplete={autoComplete}
      max={max}
      min={min}
      placeholder={placeholder}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      className={INPUT_CLASS}
    />
  );
}

export function TextArea({ value, onChange, placeholder, rows = 3 }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${INPUT_CLASS} py-2.5 resize-y`}
    />
  );
}

export function Select({ value, onChange, options }: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={INPUT_CLASS}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/**
 * The 0–10 scale control — the single most-used input in the app.
 *
 * The value is shown large and the ends are labelled, so someone can log a score
 * without reading anything. "Not set" is a real state: an untouched slider must not
 * silently record a 0, which would be a genuine data point meaning "no symptoms".
 */
export function ScaleInput({ value, onChange, min = 0, max = 10, lowLabel, highLabel }: {
  value: number | null;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  lowLabel?: string;
  highLabel?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted">{lowLabel ?? min}</span>
        <span className={`text-2xl font-semibold tabular-nums ${value === null ? 'text-muted' : 'text-ink'}`}>
          {value === null ? '–' : value}
        </span>
        <span className="text-xs text-muted">{highLabel ?? max}</span>
      </div>
      <input
        type="range"
        className="slider"
        min={min}
        max={max}
        step={1}
        value={value ?? Math.round((min + max) / 2)}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={value === null ? 'not set' : String(value)}
      />
    </div>
  );
}

/** Direction-aware delta. Colour is backed by an arrow and a word, never alone. */
export function Delta({ percent, direction, className = '' }: {
  percent: number | null;
  direction: string;
  className?: string;
}) {
  if (percent === null || direction === 'unknown') {
    return <span className={`text-muted text-sm ${className}`}>not enough data</span>;
  }
  if (direction === 'unchanged') {
    return <span className={`text-ink-2 text-sm ${className}`}>unchanged</span>;
  }

  const improved = direction === 'improved';
  return (
    <span className={`text-sm font-medium ${improved ? 'text-good' : 'text-critical'} ${className}`}>
      {improved ? '↓' : '↑'} {Math.abs(percent)}% {improved ? 'better' : 'worse'}
    </span>
  );
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-surface-2 overflow-hidden" role="progressbar"
        aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label ?? 'Progress'}>
        <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted tabular-nums w-9 text-right">{pct}%</span>
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-muted text-sm py-8 justify-center" role="status">
      <span className="w-4 h-4 rounded-full border-2 border-hairline border-t-accent animate-spin" />
      {label}
    </div>
  );
}

export function EmptyState({ title, detail, action }: { title: string; detail?: string; action?: ReactNode }) {
  return (
    <div className="text-center py-10 px-4">
      <p className="font-medium">{title}</p>
      {detail && <p className="text-sm text-ink-2 mt-1.5 max-w-xs mx-auto">{detail}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <p role="alert" className="text-sm text-critical bg-critical/10 border border-critical/30 rounded-xl px-3 py-2.5">
      {message}
    </p>
  );
}

/** A clearly-marked demo badge, so seeded fiction is never mistaken for real data. */
export function DemoBadge() {
  return (
    <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded
      bg-surface-2 text-muted border border-hairline">
      Demo
    </span>
  );
}
